import { useParams } from "react-router-dom"
import { getGameByLocalizedId } from "@/lib/games-data"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { FavoriteStar } from "@/components/ui/favorite-star"
import { ArrowLeft } from "lucide-react"
import { Link } from "react-router-dom"
import { useTranslation } from "@/hooks/use-translation"
import { SEOMeta } from "@/components/seo/seo-meta"
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb"

// Import individual game components
import {
  AdivinaLaPalabra,
  NumeroDelDia,
  MemoriaColores,
  SudokuDiario,
  SopaDeLetras,
  MemoriaDePares,
  MemoriaNumerica,
} from "@/components/games/individual"

// Game component mapping
const gameComponents: Record<string, React.ComponentType> = {
  'adivina-la-palabra': AdivinaLaPalabra,
  'numero-del-dia': NumeroDelDia,
  'memoria-colores': MemoriaColores,
  'sudoku-diario': SudokuDiario,
  'sopa-de-letras': SopaDeLetras,
  'memoria-de-pares': MemoriaDePares,
  'memoria-numerica': MemoriaNumerica,
}

export function GamePage() {
  const { gameId } = useParams<{ gameId: string }>()
  const { t, language } = useTranslation()

  // Buscar el juego usando el ID localizado
  const game = gameId ? getGameByLocalizedId(gameId, language) : undefined

  if (!game) {
    return (
      <>
        <SEOMeta
          title={t("errors.gameNotFound")}
          description="The requested game does not exist"
        />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t("errors.gameNotFound")}
            </h1>
            <Link to="/">
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("navigation.backToHome")}
              </Button>
            </Link>
          </div>
        </div>
      </>
    )
  }

  // Usar el ID base del juego para buscar el componente
  const GameComponent = gameComponents[game.id]

  if (!GameComponent) {
    return (
      <>
        <SEOMeta
          title={t("errors.gameInDevelopment")}
          description={t("errors.gameInDevelopmentDesc")}
        />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              {t("errors.gameInDevelopment")}
            </h1>
            <p className="text-gray-600 mb-4">
              {t("errors.gameInDevelopmentDesc")}
            </p>
            <Link to={`/category/${game.category}`}>
              <Button>
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("navigation.backToCategory")}
              </Button>
            </Link>
          </div>
        </div>
      </>
    )
  }

  const Icon = game.icon

  return (
    <>
      <SEOMeta
        title={`${t(`games.${game.id}.name`)} - ${t('app.title')}`}
        description={t(`games.${game.id}.description`)}
        keywords={`${t(`games.${game.id}.name`)}, ${game.tags.map(tag => t(`tags.${tag}`)).join(', ')}, ${t("navigation.dailyGames").toLowerCase()}`}
      />
      <div className="w-full">
        {/* Header - Hidden on mobile */}
        <div className="hidden sm:block">
          <div className="container mx-auto px-4 py-8">
            <Breadcrumb className="mb-4">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="max-w-[120px] sm:max-w-none truncate">
                      {t("navigation.dailyGames")}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="max-w-[120px] sm:max-w-none truncate">
                    {t(`games.${game.id}.name`)}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-primary/10 rounded-lg">
                <Icon className="h-8 w-8 text-primary" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-foreground">
                    {t(`games.${game.id}.name`)}
                  </h1>
                  <FavoriteStar toolId={game.id} size="lg" />
                </div>
                <p className="text-muted-foreground">
                  {t(`games.${game.id}.description`)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {game.tags?.map((tag) => (
                <Badge key={tag} variant="outline">
                  {t(`tags.${tag}`)}
                </Badge>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full">
          <GameComponent />
        </div>
      </div>
    </>
  )
} 